import "../styles/clientSmart.css"
import { useEffect, useMemo, useRef, useState } from "react";
import type { ClientDto } from "../api/clients";

interface Props {
  clients: ClientDto[];
  value: number | null;
  onSelect: (client: ClientDto) => void;
  onCreateNew: (prefill: string) => void;
}

export const ClientSelectSmart = ({ clients, value, onSelect, onCreateNew }: Props) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selectedClient = clients.find((c) => c.id === value) || null;

  const normalizePhone = (p?: string) => p?.replace(/\D/g, "") ?? "";
  const normalizeText = (s?: string) =>
    (s ?? "")
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/\s+/g, " ")
      .trim();

  const filtered = useMemo(() => {
    const raw = query.trim();
    if (!raw) return [];

    const hasDigits = /\d/.test(raw);

    if (hasDigits) {
      const qPhone = normalizePhone(raw);
      return clients.filter((c) => normalizePhone(c.phone).includes(qPhone));
    }

    const qText = normalizeText(raw);
    return clients.filter((c) =>
      normalizeText(`${c.firstName ?? ""} ${c.lastName ?? ""} ${c.companyName ?? ""}`)
        .includes(qText)
    );
  }, [clients, query]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
  }, [query, isOpen]);

  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll<HTMLDivElement>(".client-item");
    if (!items.length) return;
    items[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, filtered.length]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0) {
        onSelect(filtered[activeIndex]);
        setQuery("");
        setIsOpen(false);
      } else {
        onCreateNew(query);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const highlight = (text: string) => {
    const raw = normalizeText(query);
    const t = normalizeText(text);
    const idx = t.indexOf(raw);
    if (idx === -1) return text;

    return (
      <>
        {text.slice(0, idx)}
        <mark>{text.slice(idx, idx + raw.length)}</mark>
        {text.slice(idx + raw.length)}
      </>
    );
  };

  return (
    <div className="client-select">
      <input
        className="client-input"
        placeholder="Поиск клиента (ФИО / телефон)"
        value={
          isOpen
            ? query
            : selectedClient
            ? `${selectedClient.firstName ?? ""} ${selectedClient.lastName ?? ""} ${selectedClient.companyName ?? ""}`.trim()
            : ""
        }
        onFocus={() => {
          setIsOpen(true);
          setQuery("");
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onKeyDown={handleKey}
      />

      {isOpen && query.trim() !== "" && (
        <>
          {filtered.length === 0 && (
            <div className="client-list empty">
              <div className="client-empty">
                Клиент не найден
                <button
                  onMouseDown={() => onCreateNew(query)}
                  className="client-create-btn"
                >
                  Создать клиента
                </button>
              </div>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="client-list" ref={listRef}>
              {filtered.map((c, i) => (
                <div
                  key={c.id}
                  className={"client-item" + (i === activeIndex ? " active" : "")}
                  onMouseDown={() => {
                    onSelect(c);
                    setQuery("");
                    setIsOpen(false);
                  }}
                >
                  <div className="client-name">
                    {highlight(
                      `${c.firstName ?? ""} ${c.lastName ?? ""} ${c.companyName ?? ""}`.trim()
                    )}
                  </div>
                  <div className="client-phone">{c.phone}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
